import { PricingTiers } from "./pricing-data";

// Type definitions for strategies
export interface PayerStrategy {
    id: string;
    title: string;
    description: string;
    headerTitle: string;
    headerDesc: string;
    pricingTierId?: keyof typeof PricingTiers;
    cardData?: {
        title: string;
        price?: string;
        unit?: string;
        subtitle?: string;
        pill?: string;
        footer?: string;
        features: { label: string; text: string; tooltip?: string }[];
    };
    features: { title: string; desc: string }[];
    steps?: { title: string; desc: string }[];
}

// Content Structure
export const PricingContent = {
    // --- TAB 1: PROFESSIONALS ---
    professionals: {
        header: {
            title: "Extend Your Clinical Reach Beyond the Session",
            description: "The complete platform for modern neuro-rehabilitation. Build evidence-based plans in minutes, track patient progress remotely, and secure funding effortlessly.",
        },
        /* Practice Pays */
        strategies: [
            {
                id: 'practice',
                title: 'Practice',
                description: 'Transferable seats & discounts',
                headerTitle: 'Flexible Practice Licenses',
                headerDesc: 'Transferable seats for clinics, therapy providers, and fast-moving caseloads.',
                features: [
                    {
                        title: 'Rebillable Expense',
                        desc: 'Download itemized license receipts to bill back to insurers or solicitors as disbursements.'
                    },
                    {
                        title: 'Transferable Seats',
                        desc: 'Patient discharged? Instantly reassign the seat to a new client. You own the slot, not the client.'
                    },
                    {
                        title: 'Mix & Match',
                        desc: 'Combine practice-funded clients with family-funded clients on the same dashboard.'
                    }
                ],
                steps: [
                    {
                        title: "Draft Client ",
                        desc: "Create your account and build the rehab plan privately. No payment required."
                    },
                    {
                        title: "Invite & Trial",
                        desc: "One-click to invite the client to neumind with a risk-free trial period."
                    },
                    {
                        title: "Active & Invoice",
                        desc: "After trial period, client slot is added to your practice with automated biling and invoicing."
                    }
                ]
            },
            {
                id: 'client',
                title: 'Client / Family',
                description: 'They subscribe directly',
                headerTitle: 'Direct Client Subscription',
                headerDesc: 'Zero cost or admin for your practice. Clients or families subscribe directly via the app to access your care plans.',
                pricingTierId: 'active',
                cardData: {
                    title: 'Subscription Price',
                    unit: '/mo',
                    subtitle: 'Billed to client or family member.',
                    pill: 'Includes 14-Day Free Trial',
                    footer: 'Less than the cost of 15 mins of OT.',
                    features: [
                        { label: 'Unlimited Care Circle:', text: 'Invite family & therapists free' },
                        { label: 'Start Today:', text: 'Billing starts after client joins with free trial period.' }
                    ],

                },
                features: [
                    {
                        title: 'Zero Admin',
                        desc: 'We handle all billing, receipts, and support directly'
                    },
                    {
                        title: 'Continuity',
                        desc: 'Clients retain full access to data, strategies and history after discharge.'
                    },
                    {
                        title: 'Mix & Match',
                        desc: 'Manage self-funded clients alongside your practice-funded caseload in one dashboard.'
                    }
                ],
                steps: [
                    {
                        title: "Build in Draft (Free)",
                        desc: "Create your account and build the rehab plan privately. No payment required."
                    },
                    {
                        title: "Send Info & Demo",
                        desc: "Use 'Demo Mode' to show value, or send our Client Welcome Pack directly from your dashboard."
                    },
                    {
                        title: "Invite & Handover",
                        desc: "Send the invite. The client starts a risk-free trial and you are automatically connected."
                    }
                ]
            },
            {
                id: 'third-party',
                title: 'Third-Party',
                description: 'We invoice Insurers & Deputies',
                headerTitle: 'Funded Case License',
                headerDesc: 'For cases funded by Insurers, Deputies, or Solicitors. We generate the clinical justification and handle the direct invoicing.',
                pricingTierId: 'active',
                cardData: {
                    title: 'Single Client License',
                    unit: '/mo',
                    subtitle: 'Invoiced to Fundholder',
                    pill: 'Includes Funding Justification',
                    footer: 'Less than the cost of 15 mins of OT.',
                    features: [
                        { label: 'Centralise Data:', text: 'MDT, outcomes, engagement in one place' },
                        { label: 'Avoid Delay:', text: 'Start now, invoice later - don\'t let funding lags delay rehabilitation' },
                        { label: 'Seamless Transfer:', text: 'License travels with the client. Easily transfer funding responsibility without losing clinical data' }
                    ]
                },
                features: [
                    {
                        title: 'Streamline Justification',
                        desc: 'Build your client’s plan in Draft Mode and automate justification for funding approval from Deputies and Case Managers.'
                    },
                    {
                        title: 'We Handle Invoicing',
                        desc: 'Simply enter fundholder details - we manage the invoice, PO numbers, and payment chasing.'
                    },
                    {
                        title: 'Bridge Access',
                        desc: 'Start the client immediately. We provide a grace period so care isn\'t paused while funding clears.'
                    }
                ],
                steps: [
                    {
                        title: "Draft & Justify",
                        desc: "Build the client profile in draft mode to establish clinical need. Our system auto-generates a PDF 'Clinical Justification' pack."
                    },
                    {
                        title: "Send Funding Request",
                        desc: "Select 'Third-Party Pay' and enter the Case Manager's email. We automatically send them the justification, invoice, and payment link."
                    },
                    {
                        title: "Bridge the Gap",
                        desc: "Grant the client immediate access. We provide 'Bridge Access' to the app while we handle the admin delay."
                    }
                ]
            }
        ] as PayerStrategy[],
        addOns: [
            {
                id: 'setup',
                title: 'Supported Setup',
                // Note: Currency replacement happens in the component or we can change this to be dynamic if needed
                // For now, we will assume the structure allows for injection or uses a base string
                price: `One-off: {setup_price}`,
                description: 'Video call support to configure your first client and train the family.',
                actionLabel: 'Book Session',
                iconName: 'headset'
            },
            {
                id: 'integration',
                title: 'Clinical Integration',
                price: `One-off: {integration_price}`,
                description: 'Risk protocol configuration, MDT goal alignment, and team training.',
                actionLabel: 'View Scope',
                iconName: 'clipboard'
            },
            {
                id: 'workshops',
                title: 'Team Workshops',
                price: 'Enquire',
                description: 'Bespoke training sessions and workflow design for large teams.',
                actionLabel: 'Contact Us',
                iconName: 'presentation'
            }
        ]
    },

    // --- TAB 2: INDIVIDUALS ---
    individuals: {
        header: {
            title: "Reclaim Your Independence",
            description: "The neuro platform built for survivors and families. Whether you are in active rehab or moving on, we've got you covered."
        },
        steps: [
            {
                title: "Get an invite or sign up",
                description: "Your clinician or family member can invite you, or you can start directly from this page.",
            },
            {
                title: "Choose your plan",
                description: "Pick 'Active' for rehab tools or 'Life' for maintenance. Decide who pays (you, family, or insurer).",
            },
            {
                title: "Download & start",
                description: "Install Neumind on your phone or tablet. Your tools and strategies are ready to use immediately.",
            },
        ],
        addOns: [
            {
                id: 'setup',
                title: 'Supported Setup',
                price: `One-off: {setup_price}`,
                description: 'Not confident with tech? Book a 90-minute video session with our team to set up your account and train your family.',
                actionLabel: 'Add to Plan',
                iconName: 'headset'
            }
        ]
    },

    // --- TAB 3: CASE MANAGERS ---
    caseManager: {
        header: {
            title: "The Missing Link Between Clinical Sessions and Reporting",
            description: "Empower your clients, streamline your MDT, and effortlessly generate justification and tracking data."
        },
        strategy: {
            id: 'recovery-arc',
            title: 'The Recovery Arc',
            description: 'Comprehensive, reimbursable clinical package',
            headerTitle: 'The Missing Link Between Clinical Sessions and Court Reports',
            headerDesc: 'Empower your clients, streamline your MDT, and generate defensible data for deputies and solicitors. The complete digital neurorehabilitation solution for complex case management.',
            pricingTierId: 'recoveryArc',
            cardData: {
                title: 'The Recovery Arc',
                unit: '/yr',
                subtitle: 'per client (One-off payment)',
                pill: 'Reimbursable Disbursement',
                footer: 'Includes 12 months full access.',
                features: [
                    { label: 'Full Platform Access:', text: '12 months for Client, Families, and unlimited Clinicians' },
                    { label: 'Court-Ready Reporting:', text: 'Quarterly outcomes & engagement reports' },
                    { label: 'Priority Support:', text: 'Dedicated account manager' }
                ]
            },
            features: [
                {
                    title: 'Turn "Black Box" Time into Visible Progress',
                    desc: 'Between therapy sessions, clients often struggle with engagement. Neumind acts as a 24/7 digital support worker.'
                },
                {
                    title: 'Reduce Administrative Friction',
                    desc: 'Stop chasing emails. Neumind centralises the entire MDT’s goals, notes, and outcomes in one secure platform.'
                },
                {
                    title: 'Evidence for the Court of Protection',
                    desc: 'Automatically generate "Usage & Outcome Reports" designed specifically for funding justification and capacity evidence.'
                }
            ],
            steps: [
                {
                    title: "Clinical Justification",
                    desc: "We provide a pre-written Clinical Justification Pack detailing necessity and cost-benefit analysis for the Deputy."
                },
                {
                    title: "Direct Invoicing",
                    desc: "We generate a single annual invoice for the Solicitor. We support PO numbers and BACS. 'Bridge Access' grants immediate start."
                },
                {
                    title: "Portable Licensing",
                    desc: "The license belongs to the Client. If the clinical team changes, the data and tools stay with the survivor."
                }
            ]
        } as PayerStrategy,
        addOns: [
            {
                id: 'setup',
                title: 'Supported Setup',
                price: `One-off: {setup_price}`,
                description: 'Video call support to configure your first client and train the family. Essential for reducing abandonment risk.',
                actionLabel: 'Add to Plan',
                iconName: 'headset'
            },
            {
                id: 'integration',
                title: 'Neumind Access & Integration',
                price: `One-off: {integration_price}`,
                description: 'Full clinical workflow design, MDT goal alignment workshop, and written handover report for your file.',
                actionLabel: 'View Scope',
                iconName: 'clipboard'
            },
            {
                id: 'consulting',
                title: 'Bespoke Consultancy',
                price: 'Enquire',
                description: 'Digital transformation, risk protocol configuration, and team training for large Case Management firms.',
                actionLabel: 'Contact Us',
                iconName: 'presentation'
            }
        ]
    }
}
